import { Alert, Box, Snackbar } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { REDIRECT_INDICATOR, isRedirectObject } from "@/api/constants/authRedirect";
import {
  useLazyGetServiceApiValuationReportAvmValuateQuery,
  useLazyGetServiceApiValuationReportProsawebValuateQuery,
  useLazyGetServiceApiValuationReportReportQuery,
  useLazyGetServiceApiValuationReportSoneValuateQuery,
  usePostServiceApiValuationReportReportMutation,
  usePutServiceApiValuationReportRealEstateMutation,
} from "@/api/enhancedGeneratedApi";
import {
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
  ValuationHubCoreModelRealEstateProsaWebFactorsInput,
  ValuationHubCoreModelRealEstateValuationHubRealEstateWithoutAddress,
  useGetServiceApiValuationReportRealEstateContactQuery,
  useGetServiceApiValuationReportRealEstateImagesQuery,
  useGetServiceApiValuationReportRealEstateQuery,
  useGetServiceApiValuationReportStatusQuery,
} from "@/api/generatedApi";
import { BasePage, Footer, RefineRealEstate, ValuationReportConfigFormDataProvider } from "@/components";
import { DataGroupType } from "@/components/refineRealEstate/dataGroupsConstants";
import { isDataForAvmValid, isDataForSoneValid } from "@/helpers/dataValidationHelpers";
import { mapStateToApiRequestBody } from "@/helpers/propertyDataMappers";
import { getProsaWebFactorInput, getProsaWebFactorsInput, updateProsaWebIndicativeFactors } from "@/helpers/prosaWeb";
import {
  buildingDataCompletionStatus,
  energyAndPhotovoltaicCompletionStatus,
  plotDataCompletionStatus,
  propertyImagesCompletionStatus,
} from "@/helpers/realEstateDataValidationAndCompletenessHelpers";
import { getValuationReportCookies, setValuationReportCookies } from "@/helpers/valuationReportCookieHelpers";
import {
  ValuationAnalysisContent,
  ValuationAnalysisStickyHeader,
} from "@/pages/valuationPackage/valuationAnalysisPage";
import { useAppSelector } from "@/store/hooks";
import {
  BuildingStandardDeterminationMethodEnum,
  ObjectTypeEnum,
  ProductTypeEnum,
  PropertyInformation,
  RentReferenceEnum,
} from "@/types/refineRealEstate";
import { trackPageView, trackTealiumEvent } from "@/utils/tracking/usage-tracking";

import { MissingReportConfigurationModal } from "../../valuationPackage/valuationAnalysisPage/MissingReportConfigurationModal";
import { DataTransferModal } from "./DataTransferModal";
import { ValuationReportContainer } from "./ValuationReportContainer";
import { ValuationReportHeader } from "./ValuationReportHeader";
import { ValuationReportSkeleton } from "./ValuationReportSkeleton";
import { initialStateByObjectType } from "./defaultRealEstateData";
import {
  resetDerivedValuationState,
  setContact,
  setRealEstateData,
  updateAvm,
  updateDemandScore,
  updatePropertyDetails,
  updateProsaWeb,
  updateReport,
  updateSone,
} from "./valuationReportSlice";

type WithRouteMeta = {
  isUnprotected?: boolean;
};

const hasTextValue = (value?: string | null): boolean => Boolean(value?.trim());
const reportConfigurationContactFieldNames = [
  "contact_information.company_name",
  "contact_information.email",
  "contact_information.phone_number",
  "contact_information.name",
  "contact_information.house_number",
  "contact_information.street",
  "contact_information.town",
  "contact_information.post_code",
  "contact_information.website",
] as const;

const isReportConfigurationEmpty = (formId: string): boolean => {
  const reportConfigurationForm = document.getElementById(formId);

  if (!(reportConfigurationForm instanceof HTMLFormElement)) {
    return true;
  }

  return !reportConfigurationContactFieldNames.some((fieldName) => {
    const field = reportConfigurationForm.elements.namedItem(fieldName);

    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
      ? hasTextValue(field.value)
      : false;
  });
};

const ValuationReport = (): JSX.Element => {
  const location = useLocation();
  const { t } = useTranslation("ValuationReport");
  const searchParams = new URLSearchParams(location.search);

  const realEstateId = searchParams.get("realEstateId");
  const session = searchParams.get("session");
  const view = searchParams.get("view");

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const [dataGroupToRefine, setDataGroupToRefine] = useState<DataGroupType>();
  const [isCreateReportModalOpen, setIsCreateReportModalOpen] = useState(false);
  const [createdReportUrl, setCreatedReportUrl] = useState<string | null>(null);
  const [createdReportFileName, setCreatedReportFileName] = useState<string | null>(null);

  const valuationReportState = useAppSelector((state) => state.valuationReport);
  const propertyInformationState = valuationReportState.propertyInformation;
  const selectedEquipmentPriceRange = valuationReportState.selectedEquipmentPriceRange;

  const tracking = {
    onCreate: (): Promise<void> =>
      trackTealiumEvent(
        {
          event_name: "create_valuation_report",
          event_product: "valuationpush",
        },
        { includeRealEstateId: true },
      ),
    onDownload: (): Promise<void> =>
      trackTealiumEvent(
        {
          event_name: "download_valuation_report",
          event_product: "valuationpush",
        },
        { includeRealEstateId: true },
      ),
  };

  const dispatch = useDispatch();

  useEffect(() => {
    setValuationReportCookies({ realEstateId, session });
  }, [realEstateId, session]);

  useEffect(() => {
    if (view === "analysis") {
      void trackPageView("analysis");
    }
  }, [view]);

  const sessionToken = session || getValuationReportCookies().session;

  const headers = sessionToken ? { "X-Session-Token": sessionToken } : skipToken;

  const {
    data: valuationReportStatusData,
    isLoading: valuationReportStatusIsLoading,
    error: valuationReportStatusError,
    refetch: refetchStatus,
  } = useGetServiceApiValuationReportStatusQuery(headers);

  const [triggerPutRealEstate, { isLoading: isPutRealEstateLoading }] =
    usePutServiceApiValuationReportRealEstateMutation();
  const [createReportMutation, { isLoading: isReportCreateLoading }] = usePostServiceApiValuationReportReportMutation();
  const [triggerDownloadReport, { isLoading: isReportLoading, isFetching: isReportFetching }] =
    useLazyGetServiceApiValuationReportReportQuery();

  const {
    data: realEstateData,
    error: realEstateError,
    isLoading: realEstateIsLoading,
  } = useGetServiceApiValuationReportRealEstateQuery(headers);

  const {
    data: images,
    error: imagesError,
    isLoading: imagesIsLoading,
  } = useGetServiceApiValuationReportRealEstateImagesQuery(headers);

  const {
    data: contactData,
    error: contactError,
    isLoading: contactIsLoading,
  } = useGetServiceApiValuationReportRealEstateContactQuery(headers);

  const pullTimestamp = useMemo(
    () =>
      valuationReportStatusData?.real_estate_pull_timestamp
        ? new Date(valuationReportStatusData.real_estate_pull_timestamp)?.getTime()
        : undefined,
    [valuationReportStatusData?.real_estate_pull_timestamp],
  );

  const soneTimestamp = useMemo(
    () =>
      valuationReportStatusData?.sone?.timestamp
        ? new Date(valuationReportStatusData.sone.timestamp)?.getTime()
        : undefined,
    [valuationReportStatusData?.sone?.timestamp],
  );

  const avmTimestamp = useMemo(
    () =>
      valuationReportStatusData?.avm?.timestamp
        ? new Date(valuationReportStatusData.avm.timestamp)?.getTime()
        : undefined,
    [valuationReportStatusData?.avm?.timestamp],
  );

  const prosaWebTimestamp = useMemo(
    () =>
      valuationReportStatusData?.prosa_web?.timestamp
        ? new Date(valuationReportStatusData.prosa_web.timestamp)?.getTime()
        : undefined,
    [valuationReportStatusData?.prosa_web?.timestamp],
  );

  const [
    triggerSoneValuate,
    {
      data: soneValuateData,
      error: soneValuateError,
      isLoading: soneValuateIsLoading,
      isFetching: soneValuateIsFetching,
      requestId: soneRequestId,
    },
  ] = useLazyGetServiceApiValuationReportSoneValuateQuery();

  const [
    triggerAvmValuate,
    {
      data: avmValuateData,
      error: avmValuateError,
      isLoading: avmValuateIsLoading,
      isFetching: avmValuateIsFetching,
      requestId: avmRequestId,
    },
  ] = useLazyGetServiceApiValuationReportAvmValuateQuery();

  const [
    triggerProsawebValuate,
    {
      data: prosaWebValuateData,
      error: prosaWebValuateError,
      isLoading: prosaWebValuateIsLoading,
      isFetching: prosaWebValuateIsFetching,
      requestId: prosaWebRequestId,
    },
  ] = useLazyGetServiceApiValuationReportProsawebValuateQuery();

  useEffect(() => {
    if (soneValuateError) dispatch(updateSone(null));
  }, [dispatch, soneValuateError]);

  useEffect(() => {
    if (avmValuateError) {
      dispatch(updateAvm(null));
      dispatch(updateDemandScore(null));
    }
  }, [dispatch, avmValuateError]);

  useEffect(() => {
    if (prosaWebValuateError) dispatch(updateProsaWeb(null));
  }, [dispatch, prosaWebValuateError]);

  useEffect(() => {
    dispatch(updateSone(soneValuateData ? soneValuateData : null));
  }, [dispatch, soneValuateData, soneRequestId]);

  useEffect(() => {
    dispatch(updateAvm(avmValuateData ? avmValuateData : null));
    dispatch(updateDemandScore(avmValuateData?.demand_score ?? null));
  }, [dispatch, avmValuateData, avmRequestId]);

  useEffect(() => {
    dispatch(updateProsaWeb(prosaWebValuateData ?? null));
  }, [dispatch, prosaWebRequestId, prosaWebValuateData, realEstateData?.property_information?.object_type]);

  useEffect(() => {
    if (!realEstateError && !imagesError && realEstateData && images) {
      const modalTimeout = setTimeout(() => {
        setIsModalOpen(false);
      }, 1000);

      return (): void => {
        clearTimeout(modalTimeout);
      };
    }
  }, [realEstateData, images, realEstateError, imagesError]);

  useEffect(() => {
    if (valuationReportStatusData?.avm?.price?.demand_score) {
      dispatch(updateDemandScore(valuationReportStatusData.avm.price.demand_score));
    }

    if (valuationReportStatusData?.avm && pullTimestamp && avmTimestamp && pullTimestamp < avmTimestamp) {
      dispatch(updateAvm(valuationReportStatusData.avm.price));
    }

    if (valuationReportStatusData?.sone && pullTimestamp && soneTimestamp && pullTimestamp < soneTimestamp) {
      dispatch(updateSone(valuationReportStatusData.sone.price));
    }
    if (
      valuationReportStatusData?.prosa_web &&
      pullTimestamp &&
      prosaWebTimestamp &&
      pullTimestamp < prosaWebTimestamp
    ) {
      dispatch(updateProsaWeb(valuationReportStatusData.prosa_web.price));
    }
    if (
      valuationReportStatusData?.report_asking_price ||
      valuationReportStatusData?.report_file_name ||
      valuationReportStatusData?.report_timestamp
    ) {
      dispatch(
        updateReport({
          reportAskingPrice: valuationReportStatusData?.report_asking_price,
          reportFileName: valuationReportStatusData?.report_file_name,
          reportTimestamp: valuationReportStatusData?.report_timestamp,
        }),
      );
    }
  }, [
    avmTimestamp,
    dispatch,
    prosaWebTimestamp,
    pullTimestamp,
    realEstateData?.property_information?.object_type,
    soneTimestamp,
    valuationReportStatusData,
  ]);

  useEffect(() => {
    if (realEstateData && realEstateData.property_information && images) {
      const initialState = initialStateByObjectType(realEstateData.property_information.object_type);
      const propertyImages = images.map((image, index) => ({
        url: image.url,
        title: image.title ?? null,
        is_profile_picture: image.is_title_picture ?? false,
        order: index,
      }));

      const realEstateState: PropertyInformation = {
        generalInfo: {
          ...initialState.generalInfo,
          objectType: realEstateData.property_information.object_type,
          buildingType: realEstateData.property_information.building_type || initialState.generalInfo.buildingType,
          productType: ProductTypeEnum.lendingValue,
          ownership: realEstateData.property_information.ownership || initialState.generalInfo.ownership,
          actualUse: realEstateData.rental_information?.current_usage || initialState.generalInfo.actualUse,
          selfUseSuitability:
            realEstateData.rental_information?.suitable_for_own_use || initialState.generalInfo.selfUseSuitability,
        },
        plotData: {
          ...initialState.plotData,
          plotArea: realEstateData.property_information.plot_area || initialState.plotData.plotArea,
        },
        buildingData: {
          ...initialState.buildingData,
          constructionYear:
            realEstateData.property_information.construction_year || initialState.buildingData.constructionYear,
          livingArea: realEstateData.property_information.living_area || initialState.buildingData.livingArea,
          rooms: realEstateData.property_information.rooms ?? initialState.buildingData.rooms,
          floorNumber: realEstateData.property_information.floor_number ?? initialState.buildingData.floorNumber,
          houseOrApartmentType:
            realEstateData.property_information.house_or_apartment_type ||
            initialState.buildingData.houseOrApartmentType,
          numberOfFloors:
            realEstateData.property_information.number_of_floors || initialState.buildingData.numberOfFloors,
          unitsNumber: realEstateData.property_information.number_of_units || initialState.buildingData.unitsNumber,
          roof: realEstateData.property_information.roof || initialState.buildingData.roof,
          basement: realEstateData.property_information.basement || initialState.buildingData.basement,
          basementConversion:
            realEstateData.property_information.basement_conversion || initialState.buildingData.basementConversion,
          outdoorFacilities:
            realEstateData.property_information.outdoor_facilities || initialState.buildingData.outdoorFacilities,
          garden: realEstateData.property_information.garden || initialState.buildingData.garden,
          elevator: realEstateData.property_information.elevator || initialState.buildingData.elevator,
          outdoorParkingSpaces:
            realEstateData.property_information.outdoor_parking_spaces ??
            initialState.buildingData.outdoorParkingSpaces,
          garages: realEstateData.property_information.garages ?? initialState.buildingData.garages,
        },
        energyAndPhotovoltaic: {
          ...initialState.energyAndPhotovoltaic,
          energyCertificate:
            realEstateData.property_information.energy_certificate?.energy_class ||
            initialState.energyAndPhotovoltaic.energyCertificate,
          energyEfficiencyHouseStandard:
            realEstateData.property_information.energy_efficiency_house_standard ||
            initialState.energyAndPhotovoltaic.energyEfficiencyHouseStandard,
          issueDate: realEstateData.property_information.energy_certificate?.issue_date
            ? realEstateData.property_information.energy_certificate.issue_date
            : undefined,
          certificateType:
            realEstateData.property_information.energy_certificate?.certificate_type ||
            initialState.energyAndPhotovoltaic.certificateType,
          finalEnergy:
            realEstateData.property_information.energy_certificate?.final_energy_demand ||
            initialState.energyAndPhotovoltaic.finalEnergy,
          primaryEnergy:
            realEstateData.property_information.energy_certificate?.primary_energy_demand ||
            initialState.energyAndPhotovoltaic.primaryEnergy,
          CO2Emissions:
            realEstateData.property_information.energy_certificate?.co2_emission ||
            initialState.energyAndPhotovoltaic.CO2Emissions,
          constructionYear:
            realEstateData.property_information.photovoltaic?.construction_year ||
            initialState.energyAndPhotovoltaic.constructionYear,
          annualPowerYield:
            realEstateData.property_information.photovoltaic?.annual_power_yield ||
            initialState.energyAndPhotovoltaic.annualPowerYield,
        },
        buildingStandard: {
          ...initialState.buildingStandard,
          method: realEstateData.property_information.equipment?.method || initialState.buildingStandard.method,
          matrix: {
            insulatedExteriorWalls:
              realEstateData.property_information.equipment?.matrix?.insulated_exterior_walls ||
              initialState.buildingStandard.matrix?.insulatedExteriorWalls,
            storeRoom:
              realEstateData.property_information.equipment?.matrix?.store_room ||
              initialState.buildingStandard.matrix?.storeRoom,
            bathroom:
              realEstateData.property_information.equipment?.matrix?.bathroom ||
              initialState.buildingStandard.matrix?.bathroom,
            sauna:
              realEstateData.property_information.equipment?.matrix?.sauna ||
              initialState.buildingStandard.matrix?.sauna,
            roofCovering:
              realEstateData.property_information.equipment?.matrix?.roof_covering ||
              initialState.buildingStandard.matrix?.roofCovering,
            windows:
              realEstateData.property_information.equipment?.matrix?.windows ||
              initialState.buildingStandard.matrix?.windows,
            floorCovering:
              realEstateData.property_information.equipment?.matrix?.floor_covering ||
              initialState.buildingStandard.matrix?.floorCovering,
            guestToilet:
              realEstateData.property_information.equipment?.matrix?.guest_toilet ||
              initialState.buildingStandard.matrix?.guestToilet,
            heating:
              realEstateData.property_information.equipment?.matrix?.heating ||
              initialState.buildingStandard.matrix?.heating,
            heatingSystem:
              realEstateData.property_information.equipment?.matrix?.heating_system ||
              initialState.buildingStandard.matrix?.heatingSystem,
            surfaceMountedInstallation:
              realEstateData.property_information.equipment?.matrix?.surface_mounted_installation ||
              initialState.buildingStandard.matrix?.surfaceMountedInstallation,
            fireplace:
              realEstateData.property_information.equipment?.matrix?.fireplace ||
              initialState.buildingStandard.matrix?.fireplace,
            residentialArea:
              realEstateData.property_information.equipment?.matrix?.residential_area ||
              initialState.buildingStandard.matrix?.residentialArea,
          },
          value: realEstateData.property_information.equipment?.value || initialState.buildingStandard.value,
        },
        modernization: {
          ...initialState.modernization,
          method: realEstateData.property_information.modernization?.method || initialState.modernization.method,
          refurbishmentYear:
            realEstateData.property_information.modernization?.refurbishment_year ||
            initialState.modernization.refurbishmentYear,
          matrix: {
            interiorDesign:
              realEstateData.property_information.modernization?.matrix?.interior_design ||
              initialState.modernization?.matrix?.interiorDesign,
            sanitaryArea:
              realEstateData.property_information.modernization?.matrix?.sanitary_area ||
              initialState.modernization?.matrix?.sanitaryArea,
            heatingSystem:
              realEstateData.property_information.modernization?.matrix?.heating_system ||
              initialState.modernization?.matrix?.heatingSystem,
            pipes:
              realEstateData.property_information.modernization?.matrix?.pipes ||
              initialState.modernization?.matrix?.pipes,
            windows:
              realEstateData.property_information.modernization?.matrix?.windows ||
              initialState.modernization?.matrix?.windows,
            thermalInsulation:
              realEstateData.property_information.modernization?.matrix?.thermal_insulation ||
              initialState.modernization?.matrix?.thermalInsulation,
            roofCoveringAndInsulation:
              realEstateData.property_information.modernization?.matrix?.roof_covering_and_insulation ||
              initialState.modernization?.matrix?.roofCoveringAndInsulation,
            roomLayout:
              realEstateData.property_information.modernization?.matrix?.room_layout ||
              initialState.modernization?.matrix?.roomLayout,
          },
          class: realEstateData.property_information.modernization?.class || initialState.modernization.class,
          cost: realEstateData.property_information.modernization?.cost || initialState.modernization.cost,
          costDescription:
            realEstateData.property_information.modernization?.cost_description ||
            initialState.modernization.costDescription,
        },
        financialAndRentalInfo: {
          ...initialState.financialAndRentalInfo,
          price: {
            value:
              realEstateData.financial_information?.price?.value || initialState.financialAndRentalInfo.price?.value,
          },
          rent: {
            value: realEstateData.financial_information?.rent?.value || initialState.financialAndRentalInfo.rent?.value,
            reference: realEstateData.financial_information?.rent?.reference
              ? (realEstateData.financial_information?.rent?.reference as RentReferenceEnum)
              : undefined,
          },
        },
        propertyImages: {
          images: propertyImages,
          completionStatus: propertyImagesCompletionStatus(propertyImages),
        },
      };

      dispatch(
        setRealEstateData({
          address: realEstateData?.address,
          equipmentPriceRange: realEstateData?.equipment_price_range ?? null,
          selectedEquipmentPriceRange: realEstateData?.selected_equipment_price_range ?? null,
          propertyInformation: {
            ...realEstateState,
            buildingData: {
              ...realEstateState.buildingData,
              completionStatus: buildingDataCompletionStatus(
                realEstateState.buildingData,
                realEstateState.generalInfo.objectType,
                realEstateState.generalInfo.buildingType,
              ),
            },
            plotData: {
              ...realEstateState.plotData,
              completionStatus: plotDataCompletionStatus(realEstateState.plotData.plotArea),
            },
            energyAndPhotovoltaic: {
              ...realEstateState.energyAndPhotovoltaic,
              completionStatus: energyAndPhotovoltaicCompletionStatus(
                realEstateState.energyAndPhotovoltaic,
                realEstateState.buildingData.constructionYear,
              ),
            },
          },
        }),
      );

      // Mark as initialized when we first populate the state from server data
      hasInitializedData.current = true;
    }
  }, [realEstateData, images, dispatch]);

  const hasInitializedData = useRef(false);
  const isInitialLoad = useRef(true);

  const buildRealEstateUpdateBody = (
    propertyInformation = propertyInformationState,
    overrides?: Partial<ValuationHubCoreModelRealEstateValuationHubRealEstateWithoutAddress>,
  ): ValuationHubCoreModelRealEstateValuationHubRealEstateWithoutAddress => {
    if (!propertyInformation) {
      throw new Error("Missing property information");
    }

    const objectType = propertyInformation.generalInfo.objectType as ObjectTypeEnum | undefined;
    const factors = getProsaWebFactorsInput(valuationReportState.prosaWeb, objectType) ?? undefined;
    const selectedRange = selectedEquipmentPriceRange ?? undefined;

    return {
      ...mapStateToApiRequestBody(propertyInformation),
      ...(selectedRange ? { selected_equipment_price_range: selectedRange } : {}),
      ...(factors ? { factors } : {}),
      ...overrides,
    };
  };

  const savePriceRangeHandler = useCallback(
    async (range: [number, number]): Promise<void> => {
      if (!propertyInformationState || !sessionToken) {
        return;
      }

      const fallbackSelectedEquipmentPriceRange = {
        equipment: selectedEquipmentPriceRange?.equipment,
        value: selectedEquipmentPriceRange?.value,
        min: range[0],
        max: range[1],
      };

      const response = await triggerPutRealEstate({
        "X-Session-Token": sessionToken,
        body: buildRealEstateUpdateBody(propertyInformationState, {
          selected_equipment_price_range: {
            min: range[0],
            max: range[1],
          },
        }),
      }).unwrap();

      dispatch(
        setRealEstateData({
          address: response.address ?? null,
          equipmentPriceRange: response.equipment_price_range ?? null,
          selectedEquipmentPriceRange: response.selected_equipment_price_range ?? fallbackSelectedEquipmentPriceRange,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, propertyInformationState, selectedEquipmentPriceRange, sessionToken, triggerPutRealEstate],
  );

  const saveIndicativeFactorHandler = useCallback(
    async (factor: number | ValuationHubCoreModelRealEstateProsaWebFactorsInput): Promise<void> => {
      if (!propertyInformationState || !sessionToken) {
        return;
      }

      const objectType = propertyInformationState.generalInfo.objectType as ObjectTypeEnum | undefined;
      const calculationArea =
        objectType === ObjectTypeEnum.plotOfLand
          ? propertyInformationState.plotData.plotArea
          : propertyInformationState.buildingData.livingArea;
      const factors = typeof factor === "number" ? getProsaWebFactorInput(objectType, factor) : factor;

      const response = await triggerPutRealEstate({
        "X-Session-Token": sessionToken,
        body: buildRealEstateUpdateBody(propertyInformationState, {
          factors,
        }),
      }).unwrap();

      dispatch(
        setRealEstateData({
          address: response.address ?? null,
          equipmentPriceRange: response.equipment_price_range ?? valuationReportState.equipmentPriceRange ?? null,
          selectedEquipmentPriceRange: response.selected_equipment_price_range ?? selectedEquipmentPriceRange ?? null,
        }),
      );
      dispatch(
        updateProsaWeb(
          updateProsaWebIndicativeFactors(valuationReportState.prosaWeb, objectType, factors, calculationArea, {
            syncMarketValue: true,
          }),
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      propertyInformationState,
      selectedEquipmentPriceRange,
      sessionToken,
      triggerPutRealEstate,
      valuationReportState.equipmentPriceRange,
      valuationReportState.prosaWeb,
    ],
  );

  const updateRealEstateData = useCallback(async (): Promise<void> => {
    if (!propertyInformationState || !sessionToken) {
      return;
    }

    try {
      const response = await triggerPutRealEstate({
        "X-Session-Token": sessionToken,
        body: buildRealEstateUpdateBody(propertyInformationState, {
          selected_equipment_price_range: null,
          factors: null,
        }),
      }).unwrap();

      dispatch(
        setRealEstateData({
          address: response.address ?? null,
          equipmentPriceRange: response.equipment_price_range ?? null,
          selectedEquipmentPriceRange: response.selected_equipment_price_range ?? null,
        }),
      );

      const headers = { "X-Session-Token": sessionToken };
      const shouldRunSoneBeforeProsaweb =
        isDataForSoneValid(propertyInformationState) &&
        propertyInformationState.buildingStandard.method === BuildingStandardDeterminationMethodEnum.detailed;

      if (isDataForAvmValid(propertyInformationState)) {
        dispatch(
          resetDerivedValuationState({
            clearSone: shouldRunSoneBeforeProsaweb,
            clearSelectedEquipmentPriceRange: true,
          }),
        );
      }

      if (isDataForAvmValid(propertyInformationState)) {
        try {
          await triggerAvmValuate(headers).unwrap();
        } catch (error) {
          console.error("Failed to valuate AVM", error);
        }

        if (shouldRunSoneBeforeProsaweb) {
          try {
            await triggerSoneValuate(headers).unwrap();
          } catch (error) {
            console.error("Failed to valuate Sone", error);
            return;
          }
        }

        triggerProsawebValuate(headers);
      }
    } catch (error) {
      console.error("Failed to update real estate data", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    propertyInformationState,
    sessionToken,
    triggerPutRealEstate,
    triggerSoneValuate,
    triggerAvmValuate,
    triggerProsawebValuate,
  ]);

  // Effect for initial data loading - only when property data loads from server
  useEffect(() => {
    if (!propertyInformationState || !sessionToken || !hasInitializedData.current) {
      return;
    }

    const shouldUpdatePropertyParameters =
      (pullTimestamp && soneTimestamp && pullTimestamp > soneTimestamp) ||
      (pullTimestamp && avmTimestamp && pullTimestamp > avmTimestamp) ||
      (pullTimestamp && prosaWebTimestamp && pullTimestamp > prosaWebTimestamp);

    const hasExistingValuations = !!(soneTimestamp || avmTimestamp || prosaWebTimestamp);

    if (shouldUpdatePropertyParameters || !hasExistingValuations) {
      updateRealEstateData();
    }
  }, [
    propertyInformationState,
    sessionToken,
    pullTimestamp,
    soneTimestamp,
    avmTimestamp,
    prosaWebTimestamp,
    updateRealEstateData,
  ]);

  // Effect for user parameter changes
  useEffect(() => {
    if (!propertyInformationState || !sessionToken || !hasInitializedData.current) {
      return;
    }

    // Skip if this is the initial load when propertyInformationState is first populated
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    updateRealEstateData();
  }, [propertyInformationState, sessionToken, updateRealEstateData]);

  useEffect(() => {
    if (contactData) {
      dispatch(setContact(contactData));
    }
  }, [contactData, dispatch]);

  useEffect(() => {
    if (contactError) {
      setIsErrorSnackbarOpen(true);
    }
  }, [contactError]);

  const handleCloseSnackbar = (): void => {
    setIsErrorSnackbarOpen(false);
  };

  const isAuthRedirectResponse = (data: unknown): boolean => {
    return isRedirectObject(data) && data.redirectType === REDIRECT_INDICATOR;
  };

  const hasAuthRedirectResponse =
    isAuthRedirectResponse(realEstateData) ||
    isAuthRedirectResponse(images) ||
    isAuthRedirectResponse(contactData) ||
    isAuthRedirectResponse(valuationReportStatusData);

  const isRedirecting = hasAuthRedirectResponse;
  const reportConfigurationFormId = "valuation-report-analysis-configuration-form";
  const [isGenerateReportLoading, setIsGenerateReportLoading] = useState(false);
  const [isMissingReportConfigurationDialogOpen, setIsMissingReportConfigurationDialogOpen] = useState(false);

  const submitReportConfigurationForm = (): void => {
    setIsGenerateReportLoading(true);
    const reportConfigurationForm = document.getElementById(reportConfigurationFormId);

    if (reportConfigurationForm instanceof HTMLFormElement) {
      reportConfigurationForm.requestSubmit();
      return;
    }

    setIsGenerateReportLoading(false);
  };

  const scrollToReportConfiguration = (): void => {
    requestAnimationFrame(() => {
      document.getElementById("valuation-analysis-report-configuration")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const generateReport = (): void => {
    if (isReportConfigurationEmpty(reportConfigurationFormId)) {
      setIsMissingReportConfigurationDialogOpen(true);
      return;
    }

    submitReportConfigurationForm();
    tracking.onCreate();
  };

  const reportApi = {
    isLoading: isReportCreateLoading || isGenerateReportLoading || isReportLoading,
    isFetching: isReportFetching,
    async createReport(payload: ValuationHubCoreModelDtoValuationReportConfigurationDto): Promise<Blob> {
      if (!sessionToken) throw new Error("Missing session token");
      const result = await createReportMutation({ "X-Session-Token": sessionToken, body: payload });

      if ("data" in result && result.data instanceof Blob) {
        return result.data;
      }

      throw new Error("Failed to create report");
    },

    async downloadReport(): Promise<Blob> {
      if (!sessionToken) throw new Error("Missing session token");
      const result = await triggerDownloadReport({ "X-Session-Token": sessionToken });

      if ("data" in result && result.data instanceof Blob) {
        return result.data;
      }

      throw new Error("Failed to download report");
    },
  };

  if (view === "analysis") {
    return (
      <>
        <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column" }}>
          <MissingReportConfigurationModal
            open={isMissingReportConfigurationDialogOpen}
            onClose={() => {
              setIsMissingReportConfigurationDialogOpen(false);
            }}
            onGenerateAnyway={() => {
              setIsMissingReportConfigurationDialogOpen(false);
              submitReportConfigurationForm();
            }}
            onGoToConfiguration={() => {
              setIsMissingReportConfigurationDialogOpen(false);
              scrollToReportConfiguration();
            }}
          />
          <ValuationReportHeader />
          {isModalOpen || isRedirecting ? (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%" }}>
              <ValuationReportSkeleton />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%" }}>
              <ValuationReportConfigFormDataProvider
                prosaWebPriceResult={valuationReportState.prosaWeb}
                avmPriceResult={valuationReportState.avm}
                images={valuationReportState.propertyInformation?.propertyImages.images}
                contact={valuationReportState.contact}
              >
                {dataGroupToRefine && valuationReportState.propertyInformation ? (
                  <Box sx={{ pt: "90px" }}>
                    <RefineRealEstate
                      address={valuationReportState.address}
                      propertyDetails={valuationReportState.propertyInformation}
                      setRefineData={setDataGroupToRefine}
                      initiallySelectedTab={dataGroupToRefine}
                      updatePropertyDetails={(formData: PropertyInformation) =>
                        dispatch(updatePropertyDetails(formData))
                      }
                    />
                  </Box>
                ) : (
                  <>
                    <ValuationAnalysisStickyHeader topOffset="65px" />
                    <ValuationAnalysisContent
                      isLoading={
                        realEstateIsLoading ||
                        imagesIsLoading ||
                        valuationReportStatusIsLoading ||
                        prosaWebValuateIsLoading ||
                        prosaWebValuateIsFetching
                      }
                      onSavePriceRange={savePriceRangeHandler}
                      isSavingPriceRange={isPutRealEstateLoading}
                      onSaveIndicativeFactor={saveIndicativeFactorHandler}
                      isSavingIndicativeFactor={isPutRealEstateLoading}
                      onEditObjectData={() => {
                        setDataGroupToRefine("generalInfo");
                      }}
                      onGenerateReport={generateReport}
                      isGenerateReportLoading={isGenerateReportLoading || isReportCreateLoading}
                      reportConfigurationPanelProps={{
                        address: valuationReportState.address,
                        propertyInformation: valuationReportState.propertyInformation,
                        reportAskingPrice:
                          valuationReportState.propertyInformation?.financialAndRentalInfo?.price?.value ?? null,
                        prosaWebPrice: valuationReportState.prosaWeb,
                        avmPrice: valuationReportState.avm,
                        refetchStatus,
                        reportApi,
                        formId: reportConfigurationFormId,
                        onGenerateReportLoadingChange: setIsGenerateReportLoading,
                        isCreateReportModalOpen,
                        setIsCreateReportModalOpen,
                        createdReportUrl,
                        setCreatedReportUrl,
                        createdReportFileName,
                        setCreatedReportFileName,
                      }}
                    />
                  </>
                )}
              </ValuationReportConfigFormDataProvider>
            </Box>
          )}
          <Footer />
        </Box>

        <DataTransferModal
          open={isModalOpen || isRedirecting}
          isDataLoading={
            realEstateIsLoading ||
            imagesIsLoading ||
            valuationReportStatusIsLoading ||
            contactIsLoading ||
            isRedirecting
          }
          isError={!!realEstateError || !!imagesError || !!valuationReportStatusError}
        />
        <Snackbar
          open={isErrorSnackbarOpen}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
            {t("errors.contactInfoLoadFailed")}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <BasePage showSidebar={false} showFooter={true} customHeader={<ValuationReportHeader />} fullHeight={true}>
      {isModalOpen || isRedirecting ? (
        <ValuationReportSkeleton />
      ) : (
        <ValuationReportContainer
          propertyDataIsLoading={
            realEstateIsLoading || imagesIsLoading || valuationReportStatusIsLoading || !!realEstateError
          }
          marketPriceIsLoading={
            avmValuateIsLoading ||
            avmValuateIsFetching ||
            soneValuateIsLoading ||
            soneValuateIsFetching ||
            prosaWebValuateIsLoading ||
            prosaWebValuateIsFetching ||
            isPutRealEstateLoading
          }
          avmValuateIsLoading={
            soneValuateIsLoading ||
            soneValuateIsFetching ||
            avmValuateIsLoading ||
            avmValuateIsFetching ||
            isPutRealEstateLoading
          }
          refetchStatus={refetchStatus}
        />
      )}
      <DataTransferModal
        open={isModalOpen || isRedirecting}
        isDataLoading={
          realEstateIsLoading || imagesIsLoading || valuationReportStatusIsLoading || contactIsLoading || isRedirecting
        }
        isError={!!realEstateError || !!imagesError || !!valuationReportStatusError}
      />
      <Snackbar
        open={isErrorSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {t("errors.contactInfoLoadFailed")}
        </Alert>
      </Snackbar>
    </BasePage>
  );
};

export const ValuationReportPage = ValuationReport as typeof ValuationReport & WithRouteMeta;
ValuationReportPage.isUnprotected = true;
